// Faixa "Codigo de barras invalido" (pedido do Ruan, 05/10/2026).
//
// Conta os produtos ATIVOS cujo EAN a emissao da nota recusaria — a mesma regra do pre-voo da
// NFC-e/NF-e (`motivoEanInvalido`, packages/shared/src/ean.ts). Nasceu da VND-003049: um ponto no
// fim do codigo derrubou a nota com o 225 generico, e o produto so foi achado depois da venda.
// A faixa existe pra que o defeito apareca ANTES de alguem vender aquele produto.
//
// Produto SEM codigo NAO entra (decisao dele no mesmo dia): a nota sai como SEM GTIN e a SEFAZ
// aceita. Por algumas horas a faixa listou os vazios com um botao "Gerar codigo"; saiu porque o
// codigo interno gerado nao servia a nenhum consumidor do campo (ver GET /api/produtos/ean-invalido).
//
// Clicar abre a lista (codigo da tela + descricao + motivo); cada linha leva pra ficha. Sem "X":
// como as filas de estoque, o numero so cai quando alguem CORRIGE, e dispensar esconderia a fila.
// Ambar `#f59e0b` (apaga quando resolvido), abaixo do laranja de estoque errado hoje.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAlertaFaixa } from './alertas/AlertasContexto';
import { Modal } from './Modal';

interface Item { id: number; codigo_interno: string | null; descricao: string; ean: string; motivo: string }
interface Resposta { total: number; itens: Item[] }

const POLLING_MS = 5 * 60 * 1000;
const FUNDO = '#f59e0b';
const BORDA = '#d97706';

export function AlertaEanInvalidoBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [dados, setDados] = useState<Resposta | null>(null);
  const [aberto, setAberto] = useState(false);

  // Quem pode corrigir o cadastro do produto (o PUT de /api/produtos).
  const podeVer = usuario && ['admin', 'gerente', 'estoquista'].includes(usuario.perfil);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.get<Resposta>('/api/produtos/ean-invalido'));
    } catch { /* silencioso: faixa some, o resto da tela nao pode cair por causa dela */ }
  }, []);

  useEffect(() => {
    if (!podeVer) return;
    void carregar();
    const id = setInterval(() => void carregar(), POLLING_MS);
    return () => clearInterval(id);
  }, [podeVer, carregar]);

  const ativo = !!podeVer && !!dados && dados.total > 0;
  const recolhido = useAlertaFaixa(
    'ean-invalido',
    ativo ? { rotulo: 'Codigo de barras invalido', bg: FUNDO, borda: BORDA, qtd: dados!.total } : null,
  );

  function abrir() {
    setAberto(true);
    void carregar();
  }

  const lista = dados?.itens ?? [];

  return (
    <>
      {ativo && !recolhido && (
        <div
          onClick={abrir}
          style={{
            background: FUNDO, borderLeft: `4px solid ${BORDA}`, color: '#000',
            padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 13, cursor: 'pointer',
          }}
        >
          <AlertTriangle size={16} />
          <span style={{ fontWeight: 600 }}>Codigo de barras invalido:</span>
          <span>
            {dados!.total} produto{dados!.total === 1 ? '' : 's'} — a nota fiscal vai recusar quando {dados!.total === 1 ? 'ele for vendido' : 'forem vendidos'}
          </span>
          <span style={{ marginLeft: 'auto', textDecoration: 'underline' }}>Ver produtos</span>
        </div>
      )}
      <Modal
        aberto={aberto}
        onFechar={() => setAberto(false)}
        titulo="Codigo de barras invalido"
        largura={720}
        rodape={
          <button type="button" className="btn-secundario" onClick={() => setAberto(false)}>Fechar</button>
        }
      >
        {lista.length === 0 ? (
          <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
            Nenhum produto ativo com codigo de barras invalido.
          </div>
        ) : (
          <div style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 8, color: 'var(--text-muted)', fontSize: 12 }}>
              Corrija o codigo na ficha do produto, conferindo com a embalagem. Se o produto nao tem
              codigo de barras do fabricante, apague o campo: a nota sai como SEM GTIN, que a SEFAZ aceita.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', background: 'var(--table-header-bg)' }}>
                  <th style={{ padding: '6px 8px' }}>Cod.</th>
                  <th style={{ padding: '6px 8px' }}>Descricao</th>
                  <th style={{ padding: '6px 8px' }}>Problema</th>
                  <th style={{ padding: '6px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {lista.map((it) => (
                  <tr key={it.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>{it.codigo_interno ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{it.descricao}</td>
                    <td style={{ padding: '6px 8px', fontSize: 12 }}>{it.motivo}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-primario"
                        onClick={() => { setAberto(false); navigate(`/produtos/${it.id}`); }}
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </>
  );
}
